/**
 * Wann „Erneut senden" wieder etwas tut (HM-069).
 *
 * **Die Wartezeit gehört der Oberfläche, und das ist keine Nebenbemerkung.**
 * `EMAIL_CODE_RESEND_COOLDOWN_SECONDS` in `@hausmeter/domain` sagt es selbst:
 * Sie sagt, wann der Knopf wieder etwas tut. Der Server **prüft sie nicht** —
 * `RegistrationService.resend` schickt jederzeit einen neuen Code; was den
 * Missbrauch begrenzt, ist das Kontingent am Endpunkt (drei Versuche je
 * Viertelstunde und Adresse). Wer die Wartezeit nur anzeigt und den Knopf
 * bedienbar lässt, zeigt also eine Sperre, die es nirgends gibt.
 *
 * Zwei Dinge kostet der Klick währenddessen wirklich:
 *
 * **Der Code im Postfach wird ungültig.** Ein neuer Versand setzt einen neuen
 * Hash; wer die Nachricht von vorhin öffnet und deren Ziffern eintippt, bekommt
 * „Code ungültig" für einen Code, der eben noch richtig war. Genau das passiert
 * jemandem, der klickt, während die erste Mail noch zugestellt wird.
 *
 * **Und es verbraucht eines von drei Kontingenten.** Dreimal ungeduldig geklickt
 * heißt: eine Viertelstunde lang gar kein neuer Code — während daneben stand,
 * man möge 47 Sekunden warten.
 *
 * ## Warum hier gerechnet wird und nicht gegen `resendAvailableAt` direkt
 *
 * Der Server schickt einen **Zeitpunkt**, gemessen an *seiner* Uhr. Ihn gegen
 * `Date.now()` zu halten, macht die Sperre von der Uhr des Browsers abhängig —
 * und geht die eine Viertelstunde nach, wäre der Knopf eine Viertelstunde tot.
 * Das ist derselbe Einwand, aus dem der Ablauf-Countdown daneben den
 * Absendeknopf **nicht** sperrt (`Countdown` in `routes/login.tsx`): Ein
 * gesperrter Knopf an einem gültigen Vorgang ist die ärgerlichere Variante des
 * Irrtums.
 *
 * Deshalb wird aus dem Zeitpunkt einmal eine **Frist auf der eigenen Uhr**:
 * höchstens so lang, wie die Wartezeit überhaupt sein kann. Geht die Uhr nach,
 * wartet man 60 Sekunden statt einer Viertelstunde; geht sie vor, ist der Knopf
 * sofort frei — und das schadet nicht, denn der Server nimmt ohnehin an.
 */

/**
 * Wie lang die Wartezeit höchstens ist.
 *
 * **Abgeschrieben aus `EMAIL_CODE_RESEND_COOLDOWN_SECONDS`**, wie die Fristen
 * der Rechtstexte (`lib/legal.ts`): Diese App darf `@hausmeter/domain` nur für
 * Typen benutzen, und ein Fachpaket ins Browser-Bündel zu ziehen, damit eine
 * Zeile eine Sechzig kennt, ist der Preis nicht wert. Abgeschriebene Zahlen
 * veralten **still** — deshalb hält `resend-cooldown.test.ts` sie gegen ihre
 * Quelle.
 */
export const RESEND_COOLDOWN_SECONDS = 60;

/**
 * Der Zeitpunkt auf der **eigenen** Uhr, ab dem wieder gesendet werden darf.
 *
 * `receivedAt` ist der Augenblick, in dem die Antwort eintraf — von da an läuft
 * die Frist. Was der Server als Zeitpunkt nennt, wird dabei nur als Obergrenze
 * benutzt und auf die bekannte Wartezeit gedeckelt.
 *
 * **Ein unlesbarer Zeitpunkt ergibt die volle Wartezeit, nicht null.** Er sollte
 * nicht vorkommen; käme er vor, wäre „sofort wieder senden" die Auslegung, die
 * das Kontingent verbrennt.
 */
export function resendDeadline(availableAt: string, receivedAt: number): number {
  const limit = RESEND_COOLDOWN_SECONDS * 1000;
  const untilServer = new Date(availableAt).getTime() - receivedAt;

  if (Number.isNaN(untilServer)) return receivedAt + limit;

  return receivedAt + Math.min(Math.max(0, untilServer), limit);
}
